<template>
  <div
    ref="resultsWrapperRef"
    class="min-h-0 flex-1 space-y-3 p-4 text-label focus-visible:outline-none"
    :class="[
      type === AiDialogType.AgentChat
        ? 'flex flex-col'
        : 'overflow-y-auto pt-0',
      {
        'cursor-pointer': showCursorPointer,
      },
    ]"
    tabindex="-1"
  >
    <loader :is-loading="isLoading" />
    <template v-if="!isLoading">
      <no-results-view
        v-if="areResultsEmpty && type !== AiDialogType.AgentChat"
      />
      <template v-else-if="type === AiDialogType.Agents">
        <span class="text-sm font-semibold text-label uppercase">
          {{ $t("AI_AGENTS.TITLE") }}
        </span>
        <ai-agent
          v-for="agent in aiAgents"
          :key="agent.id"
          :ref="(el) => setOptionRef(el, agent.id)"
          :agent="agent"
          :is-active="selectedOption === agent.id"
          @agent:hover="selectedOption = agent.id"
          @agent:click="handleAgentClick"
        />
      </template>
      <template v-else-if="type === AiDialogType.Deals">
        <deal-search-result
          v-for="result in dealResults"
          :key="result.id"
          :ref="(el) => setOptionRef(el, result.id)"
          :result="result"
          :is-active="selectedOption === result.id"
          @deal:click="handleSelection"
          @deal:navigate="navigate"
          @deal:new-tab="openDealInNewTab"
          @deal:hover="selectedOption = result.id"
        />
      </template>
      <template v-else-if="type === AiDialogType.Sessions">
        <agent-session
          v-for="session in agentSessionInfo.previous_sessions"
          :key="session.id"
          :ref="(el) => setOptionRef(el, session.id.toString())"
          :session="session"
          :is-active="selectedOption === session.id?.toString()"
          @session:click="handleSessionClick"
          @session:hover="selectedOption = session.id?.toString()"
        />
      </template>
      <template v-else-if="type === AiDialogType.AgentChat">
        <chat-window
          v-if="agentSessionInfo.activeSession"
          :session="agentSessionInfo.activeSession"
        />
      </template>
    </template>
  </div>
  <div
    v-if="showBottomPanel"
    class="flex items-center justify-between border-t border-divider px-4 py-3"
  >
    <div class="flex items-center space-x-4">
      <div
        v-if="type !== AiDialogType.Sessions"
        class="flex items-center space-x-2"
      >
        <div class="rounded border border-disabled px-1 py-0-5 text-silver">
          @
        </div>
        <span class="text-label">{{ $t("AI_AGENTS.MENTION_AGENTS") }}</span>
      </div>
      <div v-else class="action-wrapper">
        <div class="rounded border border-disabled px-1 py-0-5 text-silver">
          /
        </div>
        <span class="text-label">{{ $t("AI_AGENTS.DEAL_SEARCH") }}</span>
      </div>
      <div class="action-wrapper">
        <div class="flex items-center space-x-1">
          <div class="icon-wrapper text-silver">
            <icon-base :icon="IconArrowLineUp" />
          </div>
          <div class="icon-wrapper text-silver">
            <icon-base :icon="IconArrowLineUp" class="rotate-180" />
          </div>
        </div>
        <span class="text-label">{{ $t("COMMON.NAVIGATE") }}</span>
      </div>
      <div class="action-wrapper">
        <div class="icon-wrapper text-silver">
          <icon-base :icon="IconArrowEnter" />
        </div>
        <span class="text-label">{{ $t("COMMON.SELECT") }}</span>
      </div>
    </div>
    <div class="flex items-center space-x-2">
      <pin-button />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useMagicKeys, useThrottleFn, whenever } from "@vueuse/core";
import { computed, onBeforeUnmount, ref, watch } from "vue";

import { storeToRefs } from "pinia";

import { AiDialogType } from "@/enums/ai";
import { AnalyticsEvent } from "@/enums/analytics";
import { useSystem } from "@/hooks/common";
import router from "@/router";
import { ROUTE_DEAL_PROGRESS } from "@/router/routes";
import { useAiAgentsStore } from "@/stores/ai/agents";
import { useAiSearchStore } from "@/stores/aiSearch";
import { useAnalyticsStore } from "@/stores/analytics";

import AgentSession from "@/components/cmdK/AgentSession.vue";
import AiAgent from "@/components/cmdK/AiAgent.vue";
import DealSearchResult from "@/components/cmdK/DealSearchResult.vue";
import NoResultsView from "@/components/cmdK/NoResultsView.vue";
import PinButton from "@/components/cmdK/PinButton.vue";
import ChatWindow from "@/components/cmdK/chat/ChatWindow.vue";
import IconArrowEnter from "@/components/icons/IconArrowEnter.vue";
import IconArrowLineUp from "@/components/icons/IconArrowLineUp.vue";
import IconBase from "@/components/ui/IconBase.vue";
import Loader from "@/components/ui/Loader.vue";

import type { AiAgentBase } from "@/models/aiAgents";
import type { Component, ComponentPublicInstance } from "vue";

const { type, isChat = false } = defineProps<{
  type: AiDialogType;
  isChat?: boolean;
}>();

const emit = defineEmits<{
  "modal:close": [];
  "agent:selected": [AiAgentBase];
  "deal:selected": [];
  "session:selected": [string];
}>();

const store = useAiSearchStore();
const aiAgentsStore = useAiAgentsStore();
const { trackEvent } = useAnalyticsStore();
const { isMacOs } = useSystem();

const { enter, meta, ctrl, shift } = useMagicKeys();

const { dealResults, aiSearchDealsLoading } = storeToRefs(store);
const { aiAgents, isAiAgentsRequestLoading, agentSessionInfo } =
  storeToRefs(aiAgentsStore);

const selectedOption = ref<string | null>(null);
const resultsWrapperRef = ref<HTMLDivElement | null>(null);
const optionRefs = ref<ComponentPublicInstance[]>([]);
const itemRefs = ref<Map<string, ComponentPublicInstance | Component>>(
  new Map()
);

const setOptionRef = (
  el: Element | ComponentPublicInstance | null,
  id: string
) => {
  if (!el) {
    return;
  }

  itemRefs.value.set(id, el);
};

const areResultsEmpty = computed(() => {
  if (Array.isArray(results.value)) {
    return !results.value.length;
  }

  if (typeof results.value === "object") {
    return !Object.keys(results.value).length;
  }

  return true;
});

const showCursorPointer = computed(
  () => type !== AiDialogType.AgentChat && !areResultsEmpty.value
);

const systemMetaKey = computed(() => (isMacOs.value ? meta.value : ctrl.value));

const isLoading = computed(
  () =>
    (aiSearchDealsLoading.value ||
      isAiAgentsRequestLoading.value ||
      agentSessionInfo.value.requestIsLoading) &&
    type !== AiDialogType.AgentChat
);

const showBottomPanel = computed(
  () => type !== AiDialogType.AgentChat && !isChat
);

const results = computed(() => {
  const options = {
    [AiDialogType.Agents]: aiAgents.value,
    [AiDialogType.Deals]: dealResults.value,
    [AiDialogType.Sessions]: agentSessionInfo.value.previous_sessions,
    [AiDialogType.AgentChat]: agentSessionInfo.value.previous_sessions, // we don't care about agent chat, only to satisfy TS
  };

  return options[type] ?? [];
});

const navigate = () => {
  trackEvent(AnalyticsEvent.CmdKDealNavigated, {
    deal_id: selectedOption.value,
  });
  router.push({
    name: ROUTE_DEAL_PROGRESS,
    params: { id: selectedOption.value },
  });
};

const handleSelection = () => {
  if (!selectedOption.value) {
    return;
  }

  if (type === AiDialogType.Agents) {
    handleAgentClick();
    return;
  }

  if (
    type === AiDialogType.Sessions &&
    !agentSessionInfo.value.isSubmittingManually
  ) {
    handleSessionClick();
    return;
  }

  if (!agentSessionInfo.value.isSubmittingManually) {
    agentSessionInfo.value.deal = selectedOption.value;
    emit("deal:selected");
    return;
  }
};

const handleAgentClick = () => {
  if (!selectedOption.value) {
    return;
  }

  const agent = aiAgents.value.find(({ id }) => id === selectedOption.value);

  if (!agent) {
    return;
  }

  emit("agent:selected", agent);
};

const openDealInNewTab = () => {
  if (!selectedOption.value) {
    return;
  }

  trackEvent(AnalyticsEvent.CmdKDealOpenedNewTab, {
    deal_id: selectedOption.value,
  });

  const { href } = router.resolve({
    name: ROUTE_DEAL_PROGRESS,
    params: { id: selectedOption.value },
  });

  const link = Object.assign(document.createElement("a"), {
    href,
    target: "_blank",
    rel: "noopener noreferrer",
  });

  link.click();
};

const handleSessionClick = () => {
  if (!selectedOption.value) {
    return;
  }

  emit("session:selected", selectedOption.value);
};

const keydownHandler = (event: KeyboardEvent) => {
  let direction: "up" | "down" | null = null;

  if (event.key === "ArrowUp") {
    direction = "up";
  } else if (event.key === "ArrowDown") {
    direction = "down";
  }

  if (!direction) {
    return;
  }

  handleArrowNavigation(direction);
};

/**
 * Handles navigation through results using arrow keys
 * @param direction - The direction to navigate: "up" or "down"
 */
const handleArrowNavigation = useThrottleFn((direction: "up" | "down") => {
  if (areResultsEmpty.value) {
    return;
  }

  agentSessionInfo.value.isSubmittingManually = false;

  // Prevent automatic scrolling when focusing
  setTimeout(() => resultsWrapperRef.value?.focus({ preventScroll: true }), 0);

  if (!selectedOption.value) {
    selectedOption.value = results.value[0].id.toString();
    return;
  }

  const index = results.value.findIndex(
    ({ id }) => id.toString() === selectedOption.value?.toString()
  );

  if (index === -1) {
    return;
  }

  let newIndex: number;
  if (direction === "up") {
    newIndex = Math.max(0, index - 1);
  } else {
    newIndex = Math.min(index + 1, results.value.length - 1);
  }

  selectedOption.value = results.value[newIndex].id.toString();
  optionRefs.value[newIndex]?.$el?.scrollIntoView({
    behavior: "smooth",
    block: "nearest",
  });

  // Focus after scrollIntoView to maintain focus without interfering with scroll positioning
  resultsWrapperRef.value?.focus();
}, 80);

document.addEventListener("keydown", keydownHandler);

whenever(enter, () => {
  if (!selectedOption.value || areResultsEmpty.value) {
    return;
  }

  if (systemMetaKey.value) {
    shift.value ? openDealInNewTab() : navigate();
    return;
  }

  handleSelection();
});

watch(
  () => [type, agentSessionInfo.value.htmlSearchContent],
  () => (selectedOption.value = null)
);

onBeforeUnmount(() => {
  document.removeEventListener("keydown", keydownHandler);
});
</script>
<style scoped>
@reference "@/assets/tailwind.css";

.icon-wrapper {
  @apply flex items-center justify-center rounded border border-divider p-1;
}

.action-wrapper {
  @apply flex items-center space-x-2;
}
</style>
